import { type ReactNode, useState } from "react";
import { HtImage, Row, Text } from "@hightouch/htui";

import { useUser } from "src/contexts/user-context";

type TileSize = "xs" | "sm" | "md";

const TILE: Record<TileSize, { box: number; radius: string; px: number }> = {
  xs: { box: 5, radius: "sm", px: 20 },
  sm: { box: 6, radius: "md", px: 24 },
  md: { box: 8, radius: "lg", px: 32 },
};

type InitialTileProps = {
  /** The name whose first letter the tile shows. */
  name: string;
  /** 20px in the product nav's header, 24px beside a single line, 32px beside a title and a subtitle. */
  size?: TileSize;
};

/** A name's first letter in a small tile, such as a workspace's or a space's, for places with no room for the name or that sit beside it. */
export const InitialTile = ({ name, size = "sm" }: InitialTileProps) => {
  const initial = name.trim().charAt(0).toUpperCase();
  // A blank tile would read as broken, so a name with no letter to show shows nothing.
  if (!initial) return null;
  return (
    <Row
      aria-hidden
      align="center"
      justify="center"
      flexShrink={0}
      boxSize={TILE[size].box}
      borderRadius={TILE[size].radius}
      bg="primary.background"
      color="primary.base"
      // Inside the tile's box, so the pale tile holds its edge on the nav's background and its hover fill.
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
    >
      {/* 12px is the smallest type size, so the 20px tile keeps the 24px tile's letter. */}
      <Text
        size={size === "md" ? "md" : "sm"}
        fontWeight="semibold"
        color="inherit"
      >
        {initial}
      </Text>
    </Row>
  );
};

type LogoTileProps = {
  /** The logo's URL, already sized for the tile. */
  src: string;
  size?: TileSize;
  /** Shown instead when the logo fails to load, so a dead link never leaves an empty box. */
  fallback: ReactNode;
};

/** An uploaded logo in the same box as {@link InitialTile}, cropped to fill it. */
export const LogoTile = ({ src, size = "sm", fallback }: LogoTileProps) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const { box, radius, px } = TILE[size];
  if (failedSrc === src) return <>{fallback}</>;
  return (
    <Row
      aria-hidden
      flexShrink={0}
      boxSize={box}
      borderRadius={radius}
      boxShadow="xs"
      overflow="hidden"
    >
      <HtImage
        src={src}
        alt=""
        width={`${px}px`}
        height={`${px}px`}
        objectFit="cover"
        onError={() => setFailedSrc(src)}
      />
    </Row>
  );
};

/** The organization's logo, the only logo the app stores, or else the initial of `name`. */
export const OrganizationLogoOrInitial = ({
  name,
  size = "sm",
}: {
  name: string;
  size?: TileSize;
}) => {
  const { user } = useUser();
  const orgLogoUrl = user?.organization?.logo_url;
  const initial = <InitialTile name={name} size={size} />;
  return orgLogoUrl ? (
    <LogoTile
      // Requested at three times the tile's width, for sharp edges on dense screens.
      src={`${orgLogoUrl}?w=${TILE[size].px * 3}`}
      size={size}
      fallback={initial}
    />
  ) : (
    initial
  );
};

/** The open workspace's mark: its organization's logo, or else the workspace's initial. */
export const WorkspaceLogo = ({ size = "sm" }: { size?: TileSize }) => {
  const { workspace } = useUser();
  return <OrganizationLogoOrInitial name={workspace?.name ?? ""} size={size} />;
};
